/**
 * Netx.Collection extends Backbone.Collection and allows us to add functionality
 * outside of the default Backbone methods and properties
 * @class
 */

import Backbone from 'backbone';
import Rpc from 'core-libs/backbone.rpc';
// import Netx from '@netx/core/bootstrap';
import Model from './netx-model';
import {URL} from '@netx/core-next/url';
import {localStore} from '@netx/core-next/instance/localStore';

const BBCollection = Backbone.Collection;
BBCollection.prototype.reset = _.wrap(
	BBCollection.prototype.reset,
	function (reset, models, options = {}) {
		// WARNING: Do not get it in your head, as I did, that we can and should trigger 'add'/'remove' events here
		// it really messes up backgrid and I am sure there is a reason why reset is used in some cases and that it
		// is so sneaky about its operations

		// With reset Backbone basically just circumvents all the official channels of removing models and does it all in hush hush secrecy
		// Two things:
		// We want tour models to know that they have been removed so they can have a chance to clean themselves up
		// We want to handle any local storage cleanup resulting from this reset as well

		!options.filtering &&
			this.each((model) => {
				// Reference to collection
				if (model.removeCollectionReference) {
					model.removeCollectionReference(this);
				}
				// Local storage
				if (_.isFunction(this.modelRemoved)) {
					this.modelRemoved(model);
				}
			});

		// Super - let backbone do its thing
		reset.call(this, models, options);

		// Let any models that came in know
		// This also deals with `new Netx.Collection( models )` as in Bacbone.Collection constructor
		// they use this.reset( models, _.extend( { silent: true }, options ) );
		this.each((model) => {
			// Reference to collection
			if (model.addCollectionReference) model.addCollectionReference(this);
			// Local storage
			if (_.isFunction(this.modelAdded)) this.modelAdded(model);
		});
	},
);

// Make a Collection we can extend from in other classes
class ES6Collection {
	/** @override */
	constructor(models, opts) {
		BBCollection.apply(this, arguments);
	}
}
_.extend(ES6Collection.prototype, BBCollection.prototype, {
	/**
	 * The underscore `indexOf` extension here is always returning -1
	 * but a vanilla `indexOf` works just fine :/
	 * @override
	 */
	indexOf(model) {
		return this.models.indexOf(model);
	},
	/**
	 * Check for our es6 instance instead
	 * @override
	 */
	_isModel(model) {
		return (
			model instanceof Backbone.ES6Model || model instanceof Backbone.Model
		);
	},
});
Backbone.ES6Collection = ES6Collection;

export class Collection extends ES6Collection {
	/** @type {boolean} */
	get isCollection() {
		return this.constructor.__isCollection__;
	}

	// Whelp
	// Need to fix in backbone.rpc - the model get's a property of `syncing`
	// whether the call is a syncable (read) or not
	// And since our loading indication relies on the `request:read` event and the `sync` event
	// we will get false loading positives by checking `this.syncing`
	// So, check for the `read` method in our current calls list
	get isSyncing() {
		return this._rpc && this.syncing && this._rpc.callsByName.read != null;
	}

	/** @type {object} */
	get methods() {
		return {};
	}

	/** @type {object} */
	get parsers() {
		return {};
	}

	/** @type {object} */
	get rpc() {
		return false;
	}

	get url() {
		return URL.api;
	}

	/** @type {object} */
	get templateProps() {
		return this;
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {};
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {};
	}

	/**
	 * @override
	 * @param {array} models Models array
	 * @param {object} options Collection options
	 */
	constructor(models, opts = {}) {
		// Circular reference
		// Model = require('./netx-model.js');

		opts.model = opts.model || Model;
		super(models, opts);

		const rpc = opts.rpc != null ? opts.rpc : this.rpc;
		if (rpc === true) {
			this._rpc = new Rpc();
		}

		this.sorter = opts.sorter;

		/**
		 * Override this in your collection if you want to store your models locally
		 * If you create two collections of the same type and wish to keep the data seperate in the store
		 * override this in your initialization of the collection and give a different name
		 * Like var secondaryNotifications = new Netx.Collection.Notifications([], { localStorageKey: 'secondary-notifications' })
		 * @note: This will only store the attributes - you can not store object references, etc
		 * @param {object} options
		 */
		this.localStorageKey = opts.localStorageKey || null;
		/** @type {number} */
		this.maxStore = opts.maxStore || 100;

		/**
		 * @type {string}
		 */
		this.filterStr = opts.filterStr || '';

		/**
		 * Tracks models in local storage
		 * @memberof module:core/lib/netx-collection
		 * @alias stored
		 * @type {array}
		 */
		this.stored = [];

		// Hey, listen!
		this.listenTo(this, 'add', this.modelAdded)
			.listenTo(this, 'change', this.modelChanged)
			.listenTo(this, 'remove', this.modelRemoved)
			.listenTo(this, 'sync', () => {
				this.syncedOnce = true;
			});

		// Load data
		// Defer to give everything a chance to finish initialization
		// TODO: deferLocalStorage property here is not used elsewhere, and is weird
		// TODO: defering pager listening
		// allows inherrting class construtors
		// to go first, not ideal, hard to test
		_.defer(() => {
			if (!this.deferLocalStorage && this.localStorageKey) {
				this.loadLocalStorage();
			}

			if (this.pager) {
				this.listenTo(this.pager, 'change:itemTotal', (pager, total) => {
					this.trigger('updatedTotal', this);
				});
			}
		});
	}

	/**
	 * We have some models that utilize the key `attributes` for things like attribute templates
	 * @override
	 * @param {number|object|string|Backbone.Model} obj Either a model, raw model attribute data, or a model cid/id
	 * @return {Backbone.Model}
	 */
	get(obj) {
		if (obj == null) {
			return void 0;
		}

		// There is no reason, I can find in the backbone code to be passing models to this - you could just use
		// `collection.get(<id/cid>)`
		// I suppose it is for brevity, they do not have to check if the object is a model or raw attributes when looping
		// over the data provided in add/reset/set, it can just check if the model exists in the collection by passing the object or model
		// But to assume that the existence of an `attributes` property makes this a model is not good for us
		if (obj.attributes && this._isModel(obj)) {
			if (obj.cid && this._byId[obj.cid]) {
				return this._byId[obj.cid];
			}
			obj = obj.attributes;
		}
		// At this point it should either by an id, cid or a raw object of attributes (set/reset/add)
		// `_byId` is an object indexed twice, once by id and then cid
		// If you are ever having issues with this method, check the state of `this._byId`.
		return this._byId[obj] || this._byId[this.modelId(obj)];
	}

	/**
	 * Add model to session storage
	 * @note It will only store the attributes (and only the attributes which do not reference other objects directly)
	 * This is because we can only store json strings in the local/session storage
	 * @param {Netx.Model} model Instance of Netx.Model
	 */
	addToLocalStorage(model) {
		if (!model || !this.localStorageKey) return;
		// Keep in bounds of this.maxStore
		this.checkMaxStore();
		// Model key
		const keyName = [this.localStorageKey, model.id].join('');
		// To persist or not
		const persist = model.storeOptions.persist;

		// Set the value
		// TODO: implement this in the model itself? (model.toLocalStorage() or similar.)
		localStore.set(
			keyName,
			this.parseLocalStorageModelOut(_.clone(model.self())),
		);
		// Add to beginning of this.stored array
		this.stored.unshift(model.id);
		// Keep until explicitly removed
		if (persist === true) {
			localStore.persist(keyName);
		}
		// Expires
		else if (_.isString(persist) || _.isNumber(persist)) {
			localStore.expire(keyName, persist);
		}
	}

	/**
	 * Enforce this.maxStore
	 * @method
	 */
	checkMaxStore() {
		if (!this.maxStore || this.stored.length < this.maxStore - 1) return;

		// Amount to rmeove
		const count = this.stored.length - this.maxStore + 1;
		// Items to remove
		const items = _.last(this.stored, count);
		// Loop over and remove them
		_.each(items, (id) => this.removeFromLocalStorage(this.get(id)));
	}

	/**
	 * Clear out session storage for this.localStorageKey
	 * @method
	 */
	clearLocalStorage() {
		if (!this.localStorageKey) return;

		// Match all my model keys
		const regexp = new RegExp(['^', this.localStorageKey, '.*'].join(''));
		localStore.remove(localStore.keys(regexp));
		this.stored = [];
	}

	/**
	 * Perform a deeper clone than the Backbone standard: also clone the collection's models.
	 * @method
	 */
	deepClone() {
		const clone = this.clone();
		clone.reset([], {silent: true});
		clone.set(this.map((model) => model.attributes));

		// Make sure our pagers match as well
		// todo: this would fail for collections
		//       that don't create a pager on init
		//       like netx-collection
		if (this.pager) {
			clone.pager.set({...this.pager.attributes});
		}

		return clone;
	}

	/**
	 * @method
	 * @param {array} ids Model ids to delete
	 */
	deleteMultiple(ids) {
		if (ids.isCollection) {
			ids = ids.ids();
		}

		return $.when.apply(
			$,
			this.chain() // .chain() lets us perform a stream of tasks.
				.filter((item) => ids.indexOf(item.id) > -1) // returns just the objects on the list.
				.invoke('destroy', {wait: true}) // calls 'destroy' on each one of those objects, waits for server response
				.value(), // returns an array of promises, one returned by each 'destroy' call.
		);
	}

	/**
	 * Now that all model views contain the [data-nx-model-id] attribute on their elements,
	 * this utility function can find a model given a DOM event from that model's view
	 */
	getModelIdFromEvent(e) {
		const id = parseInt(
			$(e.target).closest('[data-nx-model-id]').attr('data-nx-model-id'),
		);
		return this.get(id);
	}

	/**
	 * Get size information on localStorage
	 * http://blog.blakesimpson.co.uk/read/46-check-size-of-localstorage-usage-in-chrome
	 * @method
	 * @deprecate
	 * @note not to be used heavily
	 * @return {object} totals
	 */
	getLocalStorageSize() {
		let amount = 0;
		let total = 0;
		let myTotal = 0;
		const regexp = new RegExp(['^', this.localStorageKey, '.*'].join(''));

		for (let x in localStorage) {
			amount = (localStorage[x].length * 2) / 1024 / 1024;
			total += amount;
			if (x.match(regexp)) myTotal += amount;
		}

		console.log('My total: ' + myTotal.toFixed(10) + ' MB');
		console.log('Total: ' + total.toFixed(10) + ' MB');

		return {
			myTotal: myTotal,
			total: total,
		};
	}

	/**
	 * @method
	 * @return {number} Total
	 */
	getTotal() {
		if (this.length && this.pager) {
			const total = this.pager.get('itemTotal');
			if (total < this.length) {
				return this.length;
			}
			return total;
		}

		if (_.isFunction(this.assetCount)) {
			return this.assetCount();
		}

		return this.length;
	}

	/*
	 * @method
	 * @param {number} page Page number
	 * @param {object} opts Options
	 */
	gotoPage(page, opts) {
		if (page === this.curPage) {
			return new $.Deferred().resolveWith(this);
		}

		this.curPage = parseInt(page);
		// Getting a page for detail view
		return this.fetch(_.extend({duplicateQueryPolicy: 'abort'}, opts));
	}

	/**
	 * Return an array of the IDs of the models in this collection.
	 * @method
	 */
	ids() {
		return this.pluck(this.model.prototype.idAttribute);
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	isFiltered() {
		return this.filterStr !== '' || this.filtered === true;
	}

	/**
	 * Load session storage - happens at this.initialize
	 * Looks for this.localStorageKey in sessionStorage and loads the data if available
	 */
	loadLocalStorage() {
		if (!this.localStorageKey) return;

		this.trigger('request:read', this, null);

		// Reset stored array
		this.stored = [];
		// My model keys
		const regexp = new RegExp(['^', this.localStorageKey, '.*'].join(''));
		// My stored models
		const things = localStore.get(localStore.keys(regexp));
		// We have some things
		if (things && _.isArray(things)) {
			_.each(things, (t) => {
				// TODO: implement this in the model itself? (model.fromLocalStorage(t) or similar.)
				t = this.parseLocalStorageModelIn(t);
				if (!t) return;
				// Silent to avoid a loop
				// Collection hears model added- check to add to local storage - adds to local storage - check to add to loval storage - repeat repeat
				// I feel like adding an additional property isn't worth it when we have things like "silent" at our disposal
				this.add(t, {silent: true});
			});
			this.stored = this.ids();
		}
		this.trigger('sync', this);
	}

	/**
	 * Model was added to collection
	 * @method
	 * @param {Netx.Model} model Instance of Netx.Model
	 */
	modelAdded(model) {
		if (this.localStorageKey && model.storeOptions.persist) {
			this.addToLocalStorage(model);
		}
	}

	/**
	 * Model was changed in collection
	 * @method
	 * @param {Netx.Model} model Instance of Netx.Model
	 */
	modelChanged(model) {
		if (!this.localStorageKey) return;

		// Model key
		const keyName = [this.localStorageKey, model.id].join('');
		// To persist or not
		const persist = model.storeOptions.persist;

		// Set the value
		const parsed = this.parseLocalStorageModelOut(_.clone(model.self()));
		if (!parsed) return;

		localStore.set(keyName, parsed);
		// Keep until explicitly removed
		if (persist === true) {
			localStore.persist(keyName);
		}
		// Expire
		else if (_.isString(persist) || _.isNumber(persist)) {
			localStore.expire(keyName, persist);
		}
	}

	/**
	 * Model was removed from collection
	 * @method
	 * @param {Netx.Model} model Instance of Netx.Model
	 */
	modelRemoved(model) {
		if (!model || !this.localStorageKey) return;

		this.removeFromLocalStorage(model);
	}

	/**
	 * We often need to suggest a name for a new model.  "New Model" works the first time, but
	 * after that it's not a unique name any more, so we want to try "New Model 2", "New Model 3", et cetera,
	 * until we find a good one.
	 *
	 * @method
	 * @param {string} prefix      Prefix of the model name ("New Model"), or i18n key of same ("i18n.newModel")
	 * 														 (i18n key should implement the numeric index via i18next plurals: http://i18next.com/translate/pluralSimple)
	 * @param {string} attr        The 'name' attribute of this model.  If undefined, we'll try "name", "title" and "label" in that order.
	 * @returns {string}           Returns a unique name, not yet used by any model in this collection.
	 */
	makeUniqueName(prefix, attr) {
		let index = 1;
		let translate = false;
		let title = prefix;

		if (prefix.match(/^i18n\./)) {
			translate = true;
			// Translate prefix.
			title = i18n.t(prefix, {count: index});

			// If pluralization isn't working, we could get an endless loop.
			// Test that here:
			const testTitle = i18n.t(prefix, {count: index + 1});
			if (title === testTitle) {
				console.warn(
					"makeUniqueName: i18n key '" + prefix + "' is not pluralized.",
				); // http://i18next.com/translate/pluralSimple
				return title;
			}
		}

		// Check the easy case: there are no other models!
		if (!this.length) {
			return title;
		}

		// If attr wasn't given, try to guess it.
		if (!attr) {
			if (this.at(0).has('name')) {
				attr = 'name';
			} else if (this.at(0).has('title')) {
				attr = 'title';
			} else if (this.at(0).has('label')) {
				attr = 'label';
			} else {
				throw 'Undefined attr for model name';
			}
		}

		// If we can find a model matching this title, make a new title & try again.
		const obj = {
			[attr]: title,
		};
		while (this.findWhere(obj)) {
			index++;
			if (translate) {
				title = i18n.t(prefix, {count: index});
			} else {
				title = prefix + ' ' + index;
			}
			obj[attr] = title;
		}

		return title;
	}

	/**
	 * Modify the model object that came back from local storage before inserting it into the collection
	 * @method
	 * @param  {object} obj Model data object
	 * @return {object} Model data
	 */
	parseLocalStorageModelIn(obj) {
		return obj;
	}

	/**
	 * Modify the model object that is going to local storage before it does
	 * @method
	 * @param {object} obj Model attributes
	 * @return {object} Model data
	 */
	parseLocalStorageModelOut(obj) {
		return obj;
	}

	/**
	 * Remove model from session storage
	 * @method
	 * @param {Netx.Model} model Instance of Netx.Model
	 */
	removeFromLocalStorage(model) {
		if (!model || !this.localStorageKey) return;

		// Find by id in this.stored
		const idx = _.indexOf(this.stored, model.id);
		if (idx > -1) {
			this.stored.splice(idx, 1);
		}
		localStore.remove([this.localStorageKey, model.id].join(''));
	}

	/**
	 * this.self() returns just the data in a collection that's reflected in the DAM.
	 * It can be used in method definitions to omit models or model attributes that should *not* be synced.
	 * We use it for cases where the data is structured differently in the
	 * model than on the wire, and for models/collections that want to have additional attributes
	 * for local-only reasons.  .self() is intended to be called from method-definition functions,
	 * when the API call is looking for a single JSON object to represent the entire collection.
	 *
	 * The default Netx.Collection.self() implemetation uses this.invoke() to call model.self() on each model,
	 * and returns the resulting array.
	 *
	 * @return {object}
	 * @method
	 */
	self() {
		return this.invoke('self');
	}

	/**
	 * @method
	 * @param {string} str Filter
	 */
	setFilterStr(str) {
		str = str != null ? str.toString() : '';

		if (this.filterStr === str) return new $.Deferred().resolve();

		this.filterStr = str;
		// Reset to first page
		this.pager.firstPage({fetch: false});

		return this[this.preferredFetchMethod || 'fetch']({
			duplicateQueryPolicy: 'abort',
		});
	}

	/**
	 * Set sort is to be used when sorting on the server with a `sortDTO`
	 * Otherwise, use the standard backbone/backgrid sorting or override this method
	 * @method
	 * @param {string} field Field name
	 * @param {string} order Order
	 * @param {object} opts Request options
	 */
	setSort(field, order, opts) {
		if (!this.sorter) return;
		this.sorter.setSort(field, order, opts);
	}

	/**
	 * Subscribe to websocket
	 * @method
	 */
	subscribe() {
		return Model.prototype.subscribe.apply(this, arguments);
	}

	/**
	 * Message received from websocket channel
	 * @method
	 * @param {object} data Message data
	 */
	subscriptionMessage(data) {
		const model = this.get(data.id);
		switch (data.action) {
			case 'add':
				!model && this.add(data.dto);
				break;
			case 'edit':
				model ? model.set(data.dto) : this.add(data.dto);
				break;
			case 'delete':
				model && this.remove(model);
				break;
		}
	}

	/**
	 * Takes an autocomplete object and returns model with enough data
	 * for a selection interface
	 * @method
	 * @param {object} data Data (an object of autocomplete data)
	 * @return {module:core/lib/netx-model} Instance of `netx-model`
	 * @example
	 * <collection>.toSelectedItemFromAutocomplete({
	 *     id: "1",
	 *     text: "Item label"
	 * });
	 */
	toSelectedItemFromAutocomplete(data, additionalAttrs = {}, opts = {}) {
		const model = this.model;
		const modelProto = model.prototype;

		let id = parseInt(data.id);
		if (_.isNaN(id)) {
			id = data.id;
		}
		// This is where computed keys would be cool
		return new model(
			{
				[modelProto.idAttribute]: id,
				[modelProto.titleAttribute]: data.text,
				...additionalAttrs,
			},
			opts,
		);
	}

	/**
	 * Unsubscribe from websocket
	 * @method
	 */
	unsubscribe() {
		// If models have an `unsubscribe` method, call them
		if (_.isFunction(this.model.prototype.unsubscribe)) {
			this.invoke('unsubscribe');
		}
		// Super (of sorts)
		return Model.prototype.unsubscribe.apply(this, arguments);
	}

	/**
	 * @method
	 */
	batchJobComplete() {
		return Model.prototype.batchJobComplete.apply(this, arguments);
	}

	/**
	 * @method
	 */
	batchJobError() {
		return Model.prototype.batchJobError.apply(this, arguments);
	}

	/**
	 * @method
	 */
	batchJobProgress() {
		return Model.prototype.batchJobProgress.apply(this, arguments);
	}

	/**
	 * @method
	 */
	getRestLoadedMessage() {
		return Model.prototype.getRestLoadedMessage.apply(this, arguments);
	}

	/**
	 * @method
	 */
	getRestLoadingMessage() {
		return Model.prototype.getRestLoadingMessage.apply(this, arguments);
	}

	/**
	 * @method
	 */
	trackBatchJob() {
		return Model.prototype.trackBatchJob.apply(this, arguments);
	}

	/**
	 * A scoped version of jQuery.when(), that always uses 'this' as the context for the promise it returns.
	 * It's also able to accept an array of promises, so we don't need to use .apply to make it go.
	 * @method
	 */
	when() {
		return Model.prototype.when.apply(this, arguments);
	}

	/**
	 * Object Polling:
	 * pollUntil() tells any model/collection to do something repeatedly until a conditional test passes,
	 * emitting appropriate events along the way.  Use this to follow DAM changes to certain object properties,
	 * or to check status until a status change.
	 *
	 * @param function doneTest A function that returns true if polling should finish.  (Required)
	 * @param string eventPrefix Event string prefix for polling events.  If undefined, no events are triggered.
	 * @param function pollMethod A function that causes a poll.  Must return a promise.  Defaults to this.fetch;
	 * @param number pollInterval Milliseconds between polls.  Defaults to 500.
	 * @method
	 */
	pollUntil() {
		return Model.prototype.pollUntil.apply(this, arguments);
	}
	pausePolling() {
		return Model.prototype.pausePolling.apply(this, arguments);
	}
	resumePolling() {
		return Model.prototype.resumePolling.apply(this, arguments);
	}

	//////////////////////////////////////////
	// LAZYLOADING
	//////////////////////////////////////////
	/**
	 * @method
	 * @param {number} index Index
	 */
	checkReachedBeginning(index) {
		this.reachedBeginning = this.reachedBeginning || index === 0;
		return this;
	}

	/**
	 * @method
	 * @param {number} index Index
	 */
	checkReachedEnd(index) {
		this.reachedEnd =
			this.reachedEnd ||
			index + this.pager.get('pageSize') >= this.pager.get('itemTotal');
		return this;
	}

	/**
	 * @method
	 */
	initializeLazyLoad() {
		this.reachedBeginning = this.reachedEnd = false;
		this.lazyStartIndex = this.pager.get('startIndex');
		this.lowestIndex = this.highestIndex = this.lazyStartIndex;

		// No routing
		this.pager.set({
			// TODO: collections/models should not need to know about routes/routing
			routesToPage: false,
		});

		this.checkReachedBeginning(this.lazyStartIndex)
			.checkReachedEnd(this.lazyStartIndex)
			.stopListening(this.pager, 'change:startIndex', this.startIndexUpdated)
			.listenTo(this.pager, 'change:startIndex', this.startIndexUpdated);
	}

	/**
	 * @method
	 * @param {boolean} forward Forward
	 * @deprecate - unused
	 * @return {number} Items loading
	 */
	lazyLoadItems(forward) {
		if (
			this.syncing ||
			(!forward && this.reachedBeginning) ||
			(forward && this.reachedEnd)
		)
			return false;

		const pageSize = this.pager.get('pageSize');
		const page = forward
			? (this.highestIndex + pageSize) / pageSize + 1
			: (this.lowestIndex - pageSize) / pageSize + 1;

		if (page < 1 || page > this.pager.get('pageTotal')) return false;

		this.lazyLoadedForward = forward;

		// Update pager
		this.pager.set(
			{
				currentPage: page,
			},
			{
				at: forward ? this.length : 0,
				merge: true,
				remove: false,
				sort: false,
			},
		);

		// Calculate items that will load
		if (this.pager.get('pageTotal')) {
			let diff = page * pageSize - this.pager.get('itemTotal');
			if (diff < 0) diff = 0;
			return pageSize - diff;
		}

		return pageSize;
	}

	/**
	 * @method
	 * @param {module:app/models/pager} pager Pager
	 * @param {number} startIndex Start index
	 */
	startIndexUpdated(pager, startIndex) {
		// Was not able to set at lazy load initialize
		if (this.lazyStartIndex == null) {
			this.lazyStartIndex = startIndex;
		}

		if (this.lowestIndex == null || startIndex < this.lowestIndex) {
			this.lowestIndex = startIndex;
		}

		if (this.highestIndex == null || startIndex > this.highestIndex) {
			this.highestIndex = startIndex;
		}

		this.checkReachedBeginning(startIndex);
		this.checkReachedEnd(startIndex);
	}
	//////////////////////////////////////////
	// !LAZYLOADING
	//////////////////////////////////////////

	//////////////////////////////////////////
	// HOUSEKEEPING
	//////////////////////////////////////////
	addSubCollection() {
		return Model.prototype.addSubCollection.apply(this, arguments);
	}
	removeSubCollection() {
		return Model.prototype.removeSubCollection.apply(this, arguments);
	}
	addSubModel() {
		return Model.prototype.addSubModel.apply(this, arguments);
	}
	removeSubModel() {
		return Model.prototype.removeSubModel.apply(this, arguments);
	}
	/**
	 * Severs all ties to the world so it can be eligible for garbage collection
	 * @method
	 */
	garbageCollect() {
		this._rpc && this._rpc.garbageCollect();

		// Let go of any websocket channels
		this.unsubscribe();
		// Empty out models - see our reset override
		this.reset([]);
		// Kill all event listeners
		this.stopListening().off();

		if (this.pager) {
			this.pager.garbageCollect();
		}
		delete this.subModels;

		// Loop over sub collection and trigger their cleanup
		if (_.isObject(this.subCollections)) {
			_.each(this.subCollections, function (c) {
				c.garbageCollect();
			});
			delete this.subCollections;
		}

		this.garbageCollected = true;

		return this;
	}
	//////////////////////////////////////////
	// !HOUSEKEEPING
	//////////////////////////////////////////

	// TODO: Implement the class method Load() for testing
	// see Netx.Model definition for implementation guidelines'
	//
	/**
	 * @method
	 * @static
	 * unused
	 */
	static Load(selection) {
		const collection = new this();
		if (selection != null) {
			collection.selection = selection;
		}
		return collection.fetch().then(() => {
			return collection;
		});
	}

	/** @type {boolean} */
	static get __isCollection__() {
		return true;
	}

	/** @type {string} */
	static get __name__() {
		return 'Collection';
	}
}

export default Collection;
